/**
 * Top-level router for the dashboard. Public auth routes are unwrapped;
 * everything else mounts under `<RequireAuth>`. Workspace-scoped routes
 * live under `<WorkspaceRoute>` which mounts `<WorkspaceProvider>` and
 * `<WorkspaceShell>` (top bar + side nav + outlet).
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/nfr-design/frontend-architecture.md § 2-3`.
 */

import { AuthProvider } from "@cometchat-ai/auth/client";
import { Navigate, Route, Routes } from "react-router";

import { PageErrorBoundary } from "./components/page-error-boundary";

import { RootRedirect } from "./routes/_index";
import { AuthHandoffRoute } from "./routes/auth-handoff";
import { HandoffRoute } from "./routes/handoff";
import { DecisionsRoute } from "./routes/decisions";
import { HelpdeskInboxPage } from "./helpdesk/HelpdeskInboxPage";
import { HelpdeskMembersPage } from "./helpdesk/HelpdeskMembersPage";
import { HelpdeskRolesPage } from "./helpdesk/HelpdeskRolesPage";
import { HelpdeskTeamsPage } from "./helpdesk/HelpdeskTeamsPage";
import { McpRoute } from "./routes/mcp";
import { McpCreateRoute } from "./routes/mcp-create";
import { ContactCreateRoute } from "./routes/contact-create";
import { ContactDetailRoute } from "./routes/contact-detail";
import { ContactEditRoute } from "./routes/contact-edit";
import { ContactsListRoute } from "./routes/contacts";
import { ForgotPasswordRoute } from "./routes/forgot-password";
import { JoinRoute } from "./routes/join";
import { LoginRoute } from "./routes/login";
import { NoWorkspaceRoute } from "./routes/no-workspace";
import { NotFoundRoute } from "./routes/not-found";
import { OrgMembersRoute } from "./routes/org-members";
import { OrgWorkspacesRoute } from "./routes/org-workspaces";
import { RequireAuth } from "./routes/require-auth";
import { RouteAbilityGate } from "./routes/route-ability-gate";
import { SettingsAuthRoute } from "./routes/settings-auth";
import { SettingsContextVariablesRoute } from "./routes/settings-context-variables";
import { SettingsFeatureFlagsRoute } from "./routes/settings-feature-flags";
import { SettingsMemoryRoute } from "./routes/settings-memory";
import { SettingsMembersRoute } from "./routes/settings-members";
import { SettingsRolesRoute } from "./routes/settings-roles";
import { SettingsWorkspaceRoute } from "./routes/settings-workspace";
import { SignupRoute } from "./routes/signup";
import { StaffCustomersRoute } from "./routes/staff-customers";
import { VerifyEmailRoute } from "./routes/verify-email";
import { WorkspaceIndexRedirect } from "./routes/workspace-index";
import { WorkspaceRoute } from "./routes/workspace-route";

import { OnboardRoute } from "./onboarding/pages/OnboardRoute";

import { agentsRoutes } from "./agents/routes";
import { cardTemplatesRoutes } from "./card-templates/routes";
import { clientActionsRoutes } from "./client-actions/routes";
import { feedbackRoutes } from "./feedback/routes";
import { knowledgeRoutes } from "./knowledge/routes";
import { tablesRoutes } from "./tables/routes";
import { toolsRoutes } from "./tools/routes";
import { workflowsRoutes } from "./workflows/routes";

export function App() {
  return (
    <AuthProvider>
      <PageErrorBoundary>
        <Routes>
          {/* PUBLIC */}
          <Route path="/signup" element={<SignupRoute />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/forgot-password" element={<ForgotPasswordRoute />} />
          <Route path="/verify-email" element={<VerifyEmailRoute />} />
          {/*
           * TODO(magic-link): /check-email was the magic-link OTC entry
           * page; deleted alongside the controller-level magic-link
           * removal in ENG-35588. Re-add if magic-link is reintroduced.
           */}
          <Route path="/join" element={<JoinRoute />} />
          {/*
           * OAuth handoff — the API's `/api/auth/callback` 302s here with
           * `#token=<jwt>&next=...` after exchanging the WorkOS / Google
           * authorization code. The route persists the token to
           * `localStorage` and navigates to `next`. Public — no auth guard.
           */}
          <Route path="/auth/handoff" element={<AuthHandoffRoute />} />

          {/* PROTECTED */}
          <Route element={<RequireAuth />}>
            <Route path="/no-workspace" element={<NoWorkspaceRoute />} />
            {/*
             * Onboarding wizard — outside `/w/:slug` (no workspace chrome).
             * `RootRedirect` routes onboarding-mode workspaces here; the route
             * itself redirects to `/` when the surface flag is `full`.
             */}
            <Route path="/onboard" element={<OnboardRoute />} />
            {/* Internal staff / FDE console — cross-tenant, gated by
             * platform:staff (server-enforced; the route redirects non-staff). */}
            <Route path="/staff/customers" element={<StaffCustomersRoute />} />
            <Route path="/" element={<RootRedirect />} />
            <Route path="/w/:slug" element={<WorkspaceRoute />}>
              <Route index element={<WorkspaceIndexRedirect />} />
              {/*
               * Pathless layout route: a per-subtree error boundary mounted
               * inside the workspace shell's outlet. A render-time throw in
               * any page below shows the recoverable fallback card while the
               * top bar + side nav stay alive — the same isolation knowledge
               * pages already get from their per-route boundary.
               *
               * `RouteAbilityGate` inside it mirrors the nav's visibility
               * rules at the router: desk-only principals are pinned to
               * `helpdesk*`, and every other section requires the same
               * ability tuple its nav item is gated on (typed URLs included).
               */}
              <Route
                element={
                  <PageErrorBoundary>
                    <RouteAbilityGate />
                  </PageErrorBoundary>
                }
              >
                <Route path="contacts" element={<ContactsListRoute />} />
                <Route path="handoff" element={<HandoffRoute />} />
                <Route path="helpdesk" element={<HelpdeskInboxPage />} />
                <Route path="helpdesk/members" element={<HelpdeskMembersPage />} />
                <Route path="helpdesk/roles" element={<HelpdeskRolesPage />} />
                <Route path="helpdesk/teams" element={<HelpdeskTeamsPage />} />
                <Route path="decisions" element={<DecisionsRoute />} />
                <Route path="contacts/new" element={<ContactCreateRoute />} />
                <Route path="contacts/:contactId" element={<ContactDetailRoute />} />
                <Route path="contacts/:contactId/edit" element={<ContactEditRoute />} />
                {/*
                 * Workspace / Members / Authentication are top-level sidebar
                 * sections (the old `/settings` tab wrapper + Overview were
                 * removed). The redirects below keep deep links from the
                 * tabbed era working.
                 */}
                <Route path="workspace" element={<SettingsWorkspaceRoute />} />
                <Route path="members" element={<SettingsMembersRoute />} />
                <Route
                  path="helpdesk-settings"
                  element={<Navigate to="../workspace?tab=helpdesk" replace />}
                />
                {/* Support Agents page retired 2026-07-15 — profiles are now
                 * edited per-row on Helpdesk → Members. */}
                <Route
                  path="support-agents"
                  element={<Navigate to="../helpdesk/members" replace />}
                />
                <Route path="organization/members" element={<OrgMembersRoute />} />
                <Route path="organization/workspaces" element={<OrgWorkspacesRoute />} />
                <Route path="roles" element={<SettingsRolesRoute />} />
                <Route path="feature-flags" element={<SettingsFeatureFlagsRoute />} />
                <Route path="memory" element={<SettingsMemoryRoute />} />
                <Route path="context-variables" element={<SettingsContextVariablesRoute />} />
                <Route path="auth" element={<SettingsAuthRoute />} />
                <Route path="mcp" element={<McpRoute />} />
                <Route path="mcp/new" element={<McpCreateRoute />} />
                <Route path="settings" element={<Navigate to="../workspace" replace />} />
                <Route path="settings/workspace" element={<Navigate to="../workspace" replace />} />
                <Route path="settings/members" element={<Navigate to="../members" replace />} />
                <Route path="settings/auth" element={<Navigate to="../auth" replace />} />
                <Route
                  path="settings/api-keys"
                  element={<Navigate to="../auth?tab=api-keys" replace />}
                />
                <Route path="api-keys" element={<Navigate to="../auth?tab=api-keys" replace />} />
                {agentsRoutes()}
                {workflowsRoutes()}
                {cardTemplatesRoutes()}
                {knowledgeRoutes()}
                {tablesRoutes()}
                {clientActionsRoutes()}
                {toolsRoutes()}
                {feedbackRoutes()}
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<NotFoundRoute />} />
        </Routes>
      </PageErrorBoundary>
    </AuthProvider>
  );
}
